import { getAuthLocalStorage, clearAuthLocalStorage } from "../storage/localStorage";
import { navigateTo } from "../service/navigationService";
import { PUBLIC_ROUTES } from "../website/helper/constants";
import { toast } from 'react-toastify';

/**
 * @author Diksha Lalit
 * @since 08-04-2024
 * @param url API request url.
 * @param method request method.
 * @param body request body.
 * @param contentType request contentType headers.
 * @description network request manager.
 */

/**
 * Check if path looks like a product URL (clean format: /{skuId}/{slug})
 * Product URLs have numeric skuId (Alibaba) or 24-char hex (MongoDB)
 */
const isProductUrl = (path) => {
  const segments = path.split('/').filter(Boolean);
  if (segments.length < 2) return false;
  const firstSegment = segments[0];
  // Check if first segment is numeric (Alibaba offerId) or 24-char hex (MongoDB _id)
  return /^\d+$/.test(firstSegment) || /^[a-f0-9]{24}$/i.test(firstSegment);
};

const isPublicRoute = () => {
  const currentPath = window.location.pathname;
  // Check explicit public routes or clean product URLs
  return PUBLIC_ROUTES.some(route => currentPath.startsWith(route)) || isProductUrl(currentPath);
};

const handleInvalidToken = (message) => {
  if (message?.toLowerCase().includes('token') || message?.toLowerCase().includes('jwt')) {
    clearAuthLocalStorage();
    // Don't redirect to login if on public routes
    if (!isPublicRoute()) {
      // Show toast error message before redirecting
      toast.error(message || 'Session expired. Please login again.');
      navigateTo('/login');
    }
    return true;
  }
  return false;
};

export const callAPI = async (url, method, requestHeaders, body) => {
  let loginHeader = await getAuthLocalStorage().loginHeader;

  if (loginHeader && loginHeader.length > 0) {
    requestHeaders["Authorization"] = `Bearer ${loginHeader}`;
  }

  const fetchOptions = {
    method: method,
    headers: requestHeaders,
  };

  // GET/HEAD must not have a body (browser/fetch will throw)
  const canHaveBody = method !== "GET" && method !== "HEAD";
  if (canHaveBody && body !== null && body !== undefined) {
    fetchOptions.body = body;
  }

  return new Promise((resolve, reject) => {
    fetch(url, fetchOptions)
      .then(async (response) => {
        const data = await response.json();
        // If response is not ok, reject with the error data
        if (!response.ok) {
          const errorMessage = data?.message || data?.error?.message || `HTTP error! status: ${response.status}`;

          // Check for invalid token and redirect to login
          if (handleInvalidToken(errorMessage)) {
            return;
          }

          reject({
            status: response.status,
            statusText: response.statusText,
            data: data,
            message: errorMessage
          });
        } else {
          resolve(data);
        }
      })
      .catch((error) => {
        reject(error);
      });
  });
};
